import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, ChevronDownIcon, EyeIcon, XIcon, ArrowLeftIcon, FilterIcon } from 'lucide-react';
export function BulkPayments() {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [status, setStatus] = useState<'all' | 'pending' | 'in-progress' | 'completed'>('all');
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  // Bank information - hardcoded for this bank portal
  const bankInfo = {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  };
  // Generate random bank BICs
  const bankBICs = ['AMHRETAA', 'CBETETAA', 'DASHETAA', 'BUNAETAA', 'NIBIETAA', 'WEGAETAA', 'AWINETAA', 'ZEMEAA', 'COOPETAA', 'ABYSETAA', 'BERHETAA', 'ADDETAA', 'EBCETAA', 'LIOETAA', 'UNITETAA' // United Bank
  ];
  // Corporate client names for sending payments
  const corporateClients = ['Ethio Telecom', 'Ethiopian Airlines', 'Commercial Bank of Ethiopia', 'Ethiopian Electric Power', 'Ethiopian Insurance Corporation', 'Heineken Ethiopia', 'BGI Ethiopia', 'Habesha Breweries', 'Dashen Brewery', 'Meta Abo Brewery', 'Ambo Mineral Water', 'Moha Soft Drinks', 'East African Bottling', 'National Cement', 'Dangote Cement Ethiopia', 'Midroc Gold Mine', 'Ethiopian Trading Enterprise', 'Ethiopian Shipping Lines', 'Ethiopian Construction Works', 'Ethiopian Petroleum Supply Enterprise'];
  // Department names for corporate entities
  const departments = ['Finance', 'HR', 'Operations', 'IT', 'Marketing', 'Sales', 'Administration', 'Legal', 'Procurement', 'Logistics'];
  // Common Ethiopian first names
  const firstNames = ['Abebe', 'Kebede', 'Tadesse', 'Girma', 'Bekele', 'Alemayehu', 'Tesfaye', 'Haile', 'Solomon', 'Dawit', 'Yohannes', 'Mulugeta', 'Getachew', 'Dereje', 'Mekonnen', 'Tigist', 'Hiwot', 'Selamawit', 'Meseret', 'Yordanos', 'Meron', 'Rahel', 'Bethlehem', 'Kidist', 'Tsion'];
  // Common Ethiopian last names
  const lastNames = ['Abebe', 'Kebede', 'Tadesse', 'Girma', 'Bekele', 'Tesfaye', 'Haile', 'Solomon', 'Dawit', 'Yohannes', 'Mulugeta', 'Getachew', 'Dereje', 'Mekonnen', 'Wolde', 'Gebre', 'Tekle', 'Negash', 'Assefa', 'Ayele'];
  // Generate random batch payment history
  const generateRandomBatchHistory = () => {
    const types = ['Salary Payment', 'Vendor Payment', 'Dividend Payment', 'Bonus Payment', 'Refund Payment'];
    const statuses = ['Pending', 'In Progress', 'Completed'];
    const fileNames = ['Salary_Payments_May2023.csv', 'Vendor_Payments_Q2_2023.csv', 'Monthly_Salaries_June2023.xlsx', 'Quarterly_Bonuses_Q2_2023.csv', 'Annual_Dividends_2023.xlsx', 'Contractor_Payments_July2023.csv', 'Refund_Payments_May2023.xlsx', 'Supplier_Payments_June2023.csv', 'Employee_Reimbursements_May2023.xlsx', 'Commission_Payments_Q2_2023.csv', 'Pension_Contributions_June2023.xlsx', 'Tax_Payments_Q2_2023.csv', 'Insurance_Premium_Payments_May2023.xlsx', 'Partner_Disbursements_June2023.csv', 'Loan_Disbursements_July2023.xlsx', 'Scholarship_Payments_June2023.csv', 'Grant_Disbursements_Q2_2023.xlsx', 'Project_Advance_Payments_May2023.csv'];
    // Current date for realistic date generation
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    return Array.from({
      length: 30
    }, (_, i) => {
      // Generate a random date within the last 3 months
      const randomDaysAgo = Math.floor(Math.random() * 90);
      const batchDate = new Date(currentDate);
      batchDate.setDate(batchDate.getDate() - randomDaysAgo);
      const formattedDate = batchDate.toISOString().split('T')[0];
      const hours = String(Math.floor(Math.random() * 12) + 8).padStart(2, '0'); // Business hours 8AM-8PM
      const minutes = String(Math.floor(Math.random() * 60)).padStart(2, '0');
      const seconds = String(Math.floor(Math.random() * 60)).padStart(2, '0');
      const formattedTime = `${hours}:${minutes}:${seconds}`;
      const dateTime = `${formattedDate} ${formattedTime}`;
      const totalRecords = Math.floor(Math.random() * 900) + 100; // 100-1000 records
      // Status should be more likely to be completed for older batches
      let status;
      if (randomDaysAgo > 60) {
        status = Math.random() < 0.9 ? 'Completed' : 'In Progress';
      } else if (randomDaysAgo > 30) {
        status = Math.random() < 0.7 ? 'Completed' : Math.random() < 0.5 ? 'In Progress' : 'Pending';
      } else {
        status = statuses[Math.floor(Math.random() * statuses.length)];
      }
      const successRate = status === 'Completed' ? Math.floor(Math.random() * 11) + 90 :
      // 90-100% success for completed
      status === 'In Progress' ? Math.floor(Math.random() * 30) + 60 :
      // 60-90% for in progress
      0; // 0% for pending
      const successRecords = Math.round(totalRecords * successRate / 100);
      const failedRecords = totalRecords - successRecords;
      const type = types[Math.floor(Math.random() * types.length)];
      // For most batches, receiver is another bank, but sometimes it's internal
      const receiverBank = Math.random() < 0.8 ? bankBICs.filter(bic => bic !== bankInfo.code)[Math.floor(Math.random() * (bankBICs.length - 1))] : bankInfo.code;
      // Generate batch ID with year, month, day and sequence
      const batchId = `BS-${batchDate.getFullYear()}${String(batchDate.getMonth() + 1).padStart(2, '0')}${String(batchDate.getDate()).padStart(2, '0')}-${String(i + 1).padStart(3, '0')}`;
      return {
        id: batchId,
        type,
        dateTime,
        fileName: fileNames[Math.floor(Math.random() * fileNames.length)],
        totalRecords,
        status,
        successRecords,
        failedRecords,
        successRate,
        senderBank: bankInfo.code,
        receiverBank,
        batchDate: batchDate // Store the actual date object for easier filtering
      };
    });
  };
  // Generate random transactions for a batch
  const generateRandomTransactions = (batchId: string) => {
    // Find the batch to get its date
    const batch = batchHistory.find(b => b.id === batchId);
    const batchDate = batch ? batch.batchDate : new Date();
    // Payment purposes based on the batch type
    const batchType = batch ? batch.type : 'Salary Payment';
    let purposes: string[] = [];
    let fromAccountPrefix = '1000';
    let fromAccountNameGenerator = () => corporateClients[Math.floor(Math.random() * corporateClients.length)];
    // Set appropriate purposes and account prefixes based on batch type
    if (batchType.includes('Salary')) {
      purposes = ['Monthly Salary', 'Salary Payment', 'Regular Salary', 'Monthly Compensation'];
      fromAccountPrefix = '2000'; // Payroll accounts
    } else if (batchType.includes('Vendor')) {
      purposes = ['Vendor Payment', 'Supplier Payment', 'Invoice Payment', 'Service Payment', 'Goods Payment'];
      fromAccountPrefix = '3000'; // Accounts payable
    } else if (batchType.includes('Dividend')) {
      purposes = ['Dividend Payment', 'Shareholder Distribution', 'Profit Sharing', 'Annual Dividend'];
      fromAccountPrefix = '4000'; // Dividend accounts
    } else if (batchType.includes('Bonus')) {
      purposes = ['Performance Bonus', 'Annual Bonus', 'Quarterly Bonus', 'Achievement Bonus', 'Incentive Payment'];
      fromAccountPrefix = '2000'; // Payroll accounts
    } else if (batchType.includes('Refund')) {
      purposes = ['Customer Refund', 'Service Refund', 'Overpayment Refund', 'Deposit Refund', 'Fee Refund'];
      fromAccountPrefix = '5000'; // Customer accounts
    }
    // Number of transactions - either the batch total or a sample of up to 50
    const transactionCount = batch ? batch.totalRecords > 50 ? 50 : batch.totalRecords : Math.floor(Math.random() * 30) + 20;
    return Array.from({
      length: transactionCount
    }, (_, i) => {
      const amount = Math.floor(Math.random() * 19000) + 1000; // 1,000 to 20,000 ETB
      const purpose = purposes[Math.floor(Math.random() * purposes.length)];
      // Generate realistic account numbers
      const fromAccount = `${fromAccountPrefix}${Math.floor(Math.random() * 10000000)}`;
      const toAccount = `${Math.random() < 0.5 ? '6000' : '7000'}${Math.floor(Math.random() * 10000000)}`;
      // Generate realistic account names
      const fromAccountName = fromAccountNameGenerator();
      const departmentSuffix = Math.random() < 0.3 ? ` - ${departments[Math.floor(Math.random() * departments.length)]}` : '';
      const toAccountName = `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
      // Transaction date should be same as batch date or slightly before
      const txnDate = new Date(batchDate);
      txnDate.setHours(0, 0, 0, 0);
      if (Math.random() < 0.3) {
        txnDate.setDate(txnDate.getDate() - Math.floor(Math.random() * 3)); // Some transactions from 1-3 days before
      }
      // Generate transaction ID with batch ID and sequence
      const txnId = `${batchId}-TXN${String(i + 1).padStart(4, '0')}`;
      return {
        batchId,
        fromAccount,
        fromAccountName: fromAccountName + departmentSuffix,
        toAccount,
        toAccountName,
        amount,
        purpose,
        narration: `${purpose} - ${toAccountName} - ${txnDate.toISOString().split('T')[0]}`,
        txnId,
        txnDate: txnDate.toISOString().split('T')[0]
      };
    });
  };
  const batchHistory = generateRandomBatchHistory();
  // Filter batch history based on search term, date range, and status
  const filteredBatchHistory = batchHistory.filter(batch => {
    // Filter by search term
    if (searchTerm && !batch.id.toLowerCase().includes(searchTerm.toLowerCase()) && !batch.fileName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Filter by status
    if (status !== 'all') {
      if (status === 'pending' && batch.status !== 'Pending') return false;
      if (status === 'in-progress' && batch.status !== 'In Progress') return false;
      if (status === 'completed' && batch.status !== 'Completed') return false;
    }
    // Filter by date (simplified for demo)
    // In a real app, you would parse the dates and compare them properly
    if (dateRange === 'today' && !batch.dateTime.includes(new Date().toISOString().split('T')[0])) return false;
    if (dateRange === 'week') {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      if (batch.batchDate < oneWeekAgo) return false;
    }
    if (dateRange === 'month') {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      if (batch.batchDate < oneMonthAgo) return false;
    }
    if (dateRange === 'custom') {
      if (fromDate && toDate) {
        const batchDateStr = batch.dateTime.split(' ')[0];
        if (batchDateStr < fromDate || batchDateStr > toDate) return false;
      }
    }
    return true;
  });
  const handleViewDetails = (batchId: string) => {
    setSelectedBatch(batchId);
    setShowTransactionDetails(true);
  };
  const handleDownloadReport = (batchId: string) => {
    // In a real app, this would download a report for the batch
    alert(`Downloading XLSX report for batch ${batchId}`);
  };
  const handleBackToList = () => {
    setShowTransactionDetails(false);
    setSelectedBatch(null);
  };
  return <div className="space-y-6" data-id="element-315">
      {!showTransactionDetails ? <>
          <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-316">Bulk Sending</h1>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-317">
            <div className="p-6" data-id="element-318">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-319">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-320">
                  Payment History
                </h2>
                <div className="flex flex-wrap gap-3" data-id="element-321">
                  <div className="relative" data-id="element-322">
                    <button onClick={() => setShowDateFilter(!showDateFilter)} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-323">
                      {dateRange === 'today' && 'Today'}
                      {dateRange === 'week' && 'This Week'}
                      {dateRange === 'month' && 'This Month'}
                      {dateRange === 'custom' && 'Custom Range'}
                      <ChevronDownIcon size={16} className="ml-2" data-id="element-324" />
                    </button>
                    {showDateFilter && <div className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-325">
                        <div className="py-1" data-id="element-326">
                          <button onClick={() => {
                      setDateRange('today');
                      setShowDateFilter(false);
                    }} className={`block w-full text-left px-4 py-2 text-sm ${dateRange === 'today' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-327">
                            Today
                          </button>
                          <button onClick={() => {
                      setDateRange('week');
                      setShowDateFilter(false);
                    }} className={`block w-full text-left px-4 py-2 text-sm ${dateRange === 'week' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-328">
                            This Week
                          </button>
                          <button onClick={() => {
                      setDateRange('month');
                      setShowDateFilter(false);
                    }} className={`block w-full text-left px-4 py-2 text-sm ${dateRange === 'month' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-329">
                            This Month
                          </button>
                          <div className="border-t border-gray-100 my-1" data-id="element-330"></div>
                          <div className="px-4 py-2" data-id="element-331">
                            <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-332">
                              Custom Range
                            </p>
                            <div className="space-y-2" data-id="element-333">
                              <div data-id="element-334">
                                <label className="text-xs text-gray-500" data-id="element-335">
                                  From
                                </label>
                                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full mt-1 text-sm border border-gray-300 rounded-md px-2 py-1" data-id="element-336" />
                              </div>
                              <div data-id="element-337">
                                <label className="text-xs text-gray-500" data-id="element-338">
                                  To
                                </label>
                                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full mt-1 text-sm border border-gray-300 rounded-md px-2 py-1" data-id="element-339" />
                              </div>
                              <button onClick={() => {
                          if (fromDate && toDate) {
                            setDateRange('custom');
                            setShowDateFilter(false);
                          }
                        }} disabled={!fromDate || !toDate} className={`w-full mt-2 px-3 py-1 text-xs rounded-md ${fromDate && toDate ? 'bg-[#F58220] text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} data-id="element-340">
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-341">
                    <button onClick={() => setStatus('all')} className={`px-3 py-2 text-xs rounded-md ${status === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-342">
                      All Status
                    </button>
                    <button onClick={() => setStatus('pending')} className={`px-3 py-2 text-xs rounded-md ${status === 'pending' ? 'bg-blue-100 text-blue-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-343">
                      Pending
                    </button>
                    <button onClick={() => setStatus('in-progress')} className={`px-3 py-2 text-xs rounded-md ${status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-344">
                      In Progress
                    </button>
                    <button onClick={() => setStatus('completed')} className={`px-3 py-2 text-xs rounded-md ${status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-345">
                      Completed
                    </button>
                  </div>
                  <div className="relative" data-id="element-346">
                    <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-347" />
                    <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" data-id="element-348" />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-349">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-350">
                  <thead className="bg-gray-50" data-id="element-351">
                    <tr data-id="element-352">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-353">
                        Batch ID
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-354">
                        Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-355">
                        Date & Time
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-356">
                        File Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-357">
                        Records
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-358">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-359">
                        Success
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-360">
                        Failed
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-361">
                        Success Rate
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-362">
                        Sender Bank
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-363">
                        Receiver Bank
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-364">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-365">
                    {filteredBatchHistory.length > 0 ? filteredBatchHistory.map(batch => <tr key={batch.id} className="hover:bg-gray-50" data-id="element-366">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-[#F58220]" data-id="element-367">
                            {batch.id}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-368">
                            {batch.type}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-369">
                            {batch.dateTime}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-370">
                            {batch.fileName}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-371">
                            {batch.totalRecords}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap" data-id="element-372">
                            <span className={`px-2 py-1 text-xs rounded-full ${batch.status === 'Pending' ? 'bg-blue-100 text-blue-800' : batch.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-373">
                              {batch.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-374">
                            {batch.successRecords}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-375">
                            {batch.failedRecords}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-376">
                            {batch.status === 'Pending' ? <span className="text-blue-600" data-id="element-377">Pending</span> : <span data-id="element-378">{batch.successRate}%</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-379">
                            {batch.senderBank}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-380">
                            {batch.receiverBank}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-381">
                            <div className="flex items-center space-x-3" data-id="element-382">
                              <button onClick={() => handleViewDetails(batch.id)} className="text-indigo-600 hover:text-indigo-900 flex items-center" title="View Details" data-id="element-383">
                                <EyeIcon size={16} data-id="element-384" />
                              </button>
                              {batch.status === 'Completed' && <button onClick={() => handleDownloadReport(batch.id)} className="text-gray-600 hover:text-gray-900 flex items-center" title="Download Report" data-id="element-385">
                                  <DownloadIcon size={16} data-id="element-386" />
                                </button>}
                            </div>
                          </td>
                        </tr>) : <tr data-id="element-387">
                        <td colSpan={12} className="px-4 py-4 text-center text-sm text-gray-500" data-id="element-388">
                          No batch payments found matching your criteria
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </> : <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-389">
          <div className="p-6" data-id="element-390">
            <div className="flex items-center mb-6" data-id="element-391">
              <button className="mr-4 p-2 rounded-full hover:bg-gray-100" onClick={handleBackToList} data-id="element-392">
                <ArrowLeftIcon size={20} data-id="element-393" />
              </button>
              <h1 className="text-xl font-semibold text-[#1A1F36]" data-id="element-394">
                Transaction Details: {selectedBatch}
              </h1>
            </div>
            <div className="mb-6 flex justify-between items-center" data-id="element-395">
              <div className="text-sm text-gray-600" data-id="element-396">
                Showing all transactions for this batch
              </div>
              <button onClick={() => handleDownloadReport(selectedBatch!)} className="flex items-center bg-[#F58220] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#e07619]" data-id="element-397">
                <DownloadIcon size={16} className="mr-2" data-id="element-398" />
                Download Report
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-399">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-400">
                <thead className="bg-gray-50" data-id="element-401">
                  <tr data-id="element-402">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-403">
                      Transaction ID
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-404">
                      Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-405">
                      From Account
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-406">
                      From Account Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-407">
                      To Account
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-408">
                      To Account Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-409">
                      Amount
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-410">
                      Purpose
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-411">
                      Narration
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-412">
                  {generateRandomTransactions(selectedBatch!).map((txn, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-413">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-[#F58220]" data-id="element-414">
                          {txn.txnId}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-415">
                          {txn.txnDate}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-416">
                          {txn.fromAccount}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-417">
                          {txn.fromAccountName}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-418">
                          {txn.toAccount}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-419">
                          {txn.toAccountName}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-420">
                          ETB {txn.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-421">
                          {txn.purpose}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate" data-id="element-422">
                          {txn.narration}
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}